/**
 * Admin Media Library - Olympic Fit Wellness
 * Connected to Supabase Storage
 */
import { useState, useEffect } from 'react';
import { 
  Image, 
  Upload,
  Trash2,
  Search,
  Grid,
  List,
  X,
  Copy,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import api from '../../lib/api';

export default function MediaLibraryPage() {
  const [media, setMedia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [toast, setToast] = useState(null);

  // Fallback mock data when Storage is empty or not configured
  const mockMedia = [
    { id: 'media_001', name: 'hero-gym.jpg', url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400', type: 'image', size: '2.4 MB', uploaded_at: '2026-02-10' },
    { id: 'media_002', name: 'yoga-class.jpg', url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400', type: 'image', size: '1.8 MB', uploaded_at: '2026-02-09' },
    { id: 'media_003', name: 'trainer-mike.jpg', url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400', type: 'image', size: '1.2 MB', uploaded_at: '2026-02-08' },
    { id: 'media_004', name: 'spin-class.jpg', url: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400', type: 'image', size: '2.1 MB', uploaded_at: '2026-02-07' },
    { id: 'media_005', name: 'nutrition.jpg', url: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400', type: 'image', size: '1.5 MB', uploaded_at: '2026-02-06' },
    { id: 'media_006', name: 'recovery-spa.jpg', url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400', type: 'image', size: '1.9 MB', uploaded_at: '2026-02-05' }
  ];

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    setIsLoading(true);
    try {
      const images = await api.getImages();
      if (images && images.length > 0) {
        setMedia(images.map((img, idx) => ({
          id: `media_${idx}`,
          name: img.name,
          url: img.url,
          type: 'image',
          size: formatSize(img.size),
          uploaded_at: img.created_at ? new Date(img.created_at).toLocaleDateString() : 'N/A'
        })));
      } else {
        // Use mock data when storage is empty
        setMedia(mockMedia);
      }
    } catch (error) {
      console.error('Error loading media:', error);
      setMedia(mockMedia);
    } finally {
      setIsLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(Math.round(((i + 0.5) / files.length) * 100));
        
        const result = await api.uploadImage(file);
        
        if (result.url) {
          // Add to media list
          setMedia(prev => [{
            id: `media_${Date.now()}`,
            name: file.name,
            url: result.url,
            type: 'image',
            size: formatSize(result.size),
            uploaded_at: new Date().toLocaleDateString()
          }, ...prev]);
        }
        
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
      
      showToast(`${files.length} file(s) uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      showToast('Upload failed. Please try again.', 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete ${item.name}?`)) return;
    
    try {
      await api.deleteImage(item.name);
      setMedia(prev => prev.filter(m => m.id !== item.id));
      showToast('Image deleted successfully');
      if (selectedMedia?.id === item.id) {
        setSelectedMedia(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Delete failed', 'error');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    showToast('URL copied to clipboard!');
  };

  const filteredMedia = media.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="media-library">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white`}>
          {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Media Library</h1>
          <p className="text-neutral-400">Manage images and media files</p>
        </div>
        <label className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-600 transition-colors cursor-pointer">
          {isUploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              UPLOADING {uploadProgress}%
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              UPLOAD IMAGES
            </>
          )}
          <input 
            type="file" 
            multiple 
            accept="image/*" 
            onChange={handleUpload} 
            className="hidden" 
            disabled={isUploading}
          />
        </label>
      </div>

      {/* Search & View Toggle */}
      <div className="bg-neutral-800 rounded-xl p-4 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
          <input
            type="text"
            placeholder="Search media..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-3 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-[#3B82F6] text-white' : 'bg-neutral-700 text-neutral-400'}`}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-3 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-[#3B82F6] text-white' : 'bg-neutral-700 text-neutral-400'}`}
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={loadMedia}
            className="p-3 rounded-lg bg-neutral-700 text-neutral-400 hover:bg-neutral-600 transition-colors"
            title="Refresh"
          >
            <Loader2 className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div className="bg-neutral-800 rounded-xl p-4">
          <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#3B82F6] transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Media Grid/List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-[#3B82F6]" />
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="text-center py-20">
          <Image className="w-16 h-16 mx-auto text-neutral-600 mb-4" />
          <p className="text-neutral-400">No media found</p>
          <p className="text-sm text-neutral-500 mt-2">Upload images to get started</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredMedia.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedMedia(item)}
              className="group cursor-pointer"
            >
              <div className="aspect-square bg-neutral-800 rounded-xl overflow-hidden border border-neutral-700 group-hover:border-[#3B82F6] transition-colors relative">
                <img 
                  src={item.url} 
                  alt={item.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                    className="p-2 bg-red-500 rounded-lg text-white hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm text-neutral-400 truncate">{item.name}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-neutral-800 rounded-xl border border-neutral-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Preview</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Name</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Size</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Uploaded</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-neutral-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-700">
              {filteredMedia.map((item) => (
                <tr key={item.id} className="hover:bg-neutral-700/50">
                  <td className="px-6 py-4">
                    <img 
                      src={item.url} 
                      alt={item.name} 
                      className="w-12 h-12 object-cover rounded"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400';
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 text-white">{item.name}</td>
                  <td className="px-6 py-4 text-neutral-400">{item.size}</td>
                  <td className="px-6 py-4 text-neutral-400">{item.uploaded_at}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => copyUrl(item.url)}
                        className="p-2 hover:bg-neutral-600 rounded-lg"
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4 text-neutral-400" />
                      </button>
                      <a 
                        href={item.url} 
                        download={item.name}
                        className="p-2 hover:bg-neutral-600 rounded-lg" 
                        title="Download"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Download className="w-4 h-4 text-neutral-400" />
                      </a>
                      <button 
                        onClick={() => handleDelete(item)}
                        className="p-2 hover:bg-red-500/20 rounded-lg" 
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Media Detail Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-neutral-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-neutral-700">
              <h3 className="text-lg font-semibold text-white">{selectedMedia.name}</h3>
              <button onClick={() => setSelectedMedia(null)} className="p-2 hover:bg-neutral-700 rounded-lg">
                <X className="w-5 h-5 text-neutral-400" />
              </button>
            </div>
            <div className="p-4">
              <img 
                src={selectedMedia.url} 
                alt={selectedMedia.name}
                className="w-full max-h-96 object-contain rounded-lg bg-neutral-900"
              />
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-neutral-400">Size:</span>
                  <span className="ml-2 text-white">{selectedMedia.size}</span>
                </div>
                <div>
                  <span className="text-neutral-400">Uploaded:</span>
                  <span className="ml-2 text-white">{selectedMedia.uploaded_at}</span>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm text-neutral-400">URL</label>
                <div className="flex gap-2 mt-1">
                  <input 
                    type="text" 
                    value={selectedMedia.url} 
                    readOnly
                    className="flex-1 px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg text-white text-sm"
                  />
                  <button 
                    onClick={() => copyUrl(selectedMedia.url)}
                    className="px-4 py-2 bg-[#3B82F6] text-white rounded-lg hover:bg-blue-600"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleDelete(selectedMedia)}
                  className="flex-1 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
